import { formatISO } from "date-fns";

import type {
  InsightsScheduledReportAttributes,
  InsightsScheduledReportDeliveryWeekday,
} from "shared/modules/InsightsScheduledReports/types";
import {
  InsightsScheduledSpecification,
  type InstantInsightsReport,
} from "shared/resources";
import ScheduledReportSpecificationSchedule from "shared/resources/ScheduledReportSpecificationSchedule";
import { type CurrentUser } from "shared/stores/user/types";
import type { Maybe } from "shared/types";

type ScheduledReportWeekdayFlags = Pick<
  ScheduledReportSpecificationSchedule,
  | "onFriday"
  | "onMonday"
  | "onSaturday"
  | "onSunday"
  | "onThursday"
  | "onTuesday"
  | "onWednesday"
>;

export function buildScheduledReportSpecification(
  reportAttributes: InsightsScheduledReportAttributes,
  dashboard: InstantInsightsReport,
  user: CurrentUser
): InsightsScheduledSpecification {
  const reportLabel = getReportLabel(reportAttributes, dashboard);

  return new InsightsScheduledSpecification({
    completedAt: new Date().toISOString(),
    description: reportAttributes.report.summary || "",
    enabled: true,
    hideKeyInsightsPages: reportAttributes.report.hideKeyInsightsPages ?? false,
    instantInsightsReportId: Number(dashboard.id),
    language: reportAttributes.report.language,
    organisationBrandId: reportAttributes.report.organisationBrandId ?? null,
    recipients: getRecipients(reportAttributes),
    schedules: [
      buildScheduledReportSpecificationSchedule(reportAttributes, user),
    ],
    subject: reportLabel,
    title: reportLabel,
    userId: Number(user.id),
  });
}

export function buildScheduledReportSpecificationSchedule(
  reportAttributes: InsightsScheduledReportAttributes,
  user: CurrentUser
): ScheduledReportSpecificationSchedule {
  const { delivery, reportPeriod } = reportAttributes;

  const fortnightStartDate = getFortnightStartDate(reportAttributes);

  return new ScheduledReportSpecificationSchedule({
    contentRangeDay: reportPeriod.contentRangeDay,
    contentRangeHour: reportPeriod.contentRangeHour,
    contentRangeMinute: reportPeriod.contentRangeMinute,
    daysOfMonth: getDaysOfMonth(reportAttributes),
    deliverAt: getDeliverAt(reportAttributes, user),
    deliverEvery: 0,
    deliverTo: 0,
    ...(fortnightStartDate ? { fortnightStartDate } : {}),
    frequency: delivery.frequency,
    onLastDay: delivery.monthlyDay === "last_day",
    sinceLastMonth: reportPeriod.sinceLastMonth,
    sinceThisMonth: reportPeriod.sinceThisMonth,
    ...getWeekdayFlags(delivery.daysOfWeek),
  });
}

export function getDaysOfMonth(
  reportAttributes: InsightsScheduledReportAttributes
): number[] {
  const { monthlyCustomDay, monthlyDay } = reportAttributes.delivery;

  if (monthlyDay === "first_day") {
    return [1];
  }

  if (monthlyDay === "day_15") {
    return [15];
  }

  if (monthlyDay === "custom") {
    return [monthlyCustomDay];
  }

  return [];
}

export function getDeliverAt(
  reportAttributes: InsightsScheduledReportAttributes,
  user: CurrentUser
): number {
  const { deliveryDateTime, frequency } = reportAttributes.delivery;

  if (frequency === "once") {
    return Math.floor(deliveryDateTime.getTime() / 1000);
  }

  return getSecondsSinceMidnight(deliveryDateTime, user.time_zone);
}

export function getFortnightStartDate(
  reportAttributes: InsightsScheduledReportAttributes
): Maybe<string> {
  const { deliveryDateTime, frequency } = reportAttributes.delivery;

  if (frequency !== "fortnightly") {
    return undefined;
  }

  return formatISO(deliveryDateTime);
}

export function getRecipients(
  reportAttributes: InsightsScheduledReportAttributes
): string[] {
  return [
    ...reportAttributes.delivery.recipients,
    ...reportAttributes.delivery.distributionLists.map(
      (distributionList) => distributionList.referenceEmail
    ),
  ];
}

export function getReportLabel(
  reportAttributes: InsightsScheduledReportAttributes,
  dashboard: InstantInsightsReport
): string {
  return reportAttributes.report.label || dashboard.name;
}

export function getSecondsSinceMidnight(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat("en-AU", {
    hour: "numeric",
    hour12: false,
    minute: "numeric",
    second: "numeric",
    timeZone,
  });

  return formatter.formatToParts(date).reduce((seconds, part) => {
    if (part.type === "hour") {
      return seconds + (Number(part.value) % 24) * 60 * 60;
    }

    if (part.type === "minute") {
      return seconds + Number(part.value) * 60;
    }

    if (part.type === "second") {
      return seconds + Number(part.value);
    }

    return seconds;
  }, 0);
}

export function getWeekdayFlags(
  daysOfWeek: InsightsScheduledReportDeliveryWeekday[]
): ScheduledReportWeekdayFlags {
  const selectedWeekdays = new Set(daysOfWeek);

  return {
    onFriday: selectedWeekdays.has("onFriday"),
    onMonday: selectedWeekdays.has("onMonday"),
    onSaturday: selectedWeekdays.has("onSaturday"),
    onSunday: selectedWeekdays.has("onSunday"),
    onThursday: selectedWeekdays.has("onThursday"),
    onTuesday: selectedWeekdays.has("onTuesday"),
    onWednesday: selectedWeekdays.has("onWednesday"),
  };
}
